import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { MessageSquare, X, Send, Loader2, Minimize2 } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { getBackendUrl } from '../utils/getBackendUrl';

const BACKEND_URL = getBackendUrl();
const API = `${BACKEND_URL}/api`;

export const ClientChatbot = ({ isOpen = false, onClose = null }) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: "Hello! I'm your Dawal Construction AI assistant. I can help you with project updates, construction terminology, and general inquiries. How can I assist you today?"
    }
  ]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    
    // Add user message to chat
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/chatbot`,
        {
          message: userMessage,
          session_id: sessionId
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      // Update session ID if this is first message
      if (!sessionId) {
        setSessionId(response.data.session_id);
      }

      // Add AI response to chat
      setMessages(prev => [
        ...prev,
        { role: 'assistant', content: response.data.response }
      ]);
    } catch (error) {
      console.error('Chatbot error:', error);
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: "Sorry, I'm having trouble right now. Please try again or contact your project manager."
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) {
    return null; // Don't show anything when closed (controlled by Navigation)
  }

  if (isMinimized) {
    return null; // When minimized, user can reopen from nav bar
  }

  return (
    <div 
      className="fixed bottom-20 right-2 sm:right-4 w-[calc(100%-1rem)] sm:w-[420px] h-[calc(100vh-9rem)] sm:h-[600px] max-h-[calc(100vh-9rem)] bg-white rounded-2xl flex flex-col z-50 border border-slate-300"
      style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}
    >
      {/* Header */}
      <div className="bg-slate-900 text-white p-4 sm:p-5 sm:rounded-t-2xl flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="bg-blue-500 p-2 rounded-lg">
            <MessageSquare className="w-5 h-5" />
          </div>
          <div>
            <h3 className="font-bold text-base">Dawal AI Assistant</h3>
            <p className="text-xs text-slate-300 flex items-center gap-1">
              <span className="w-2 h-2 bg-green-400 rounded-full"></span>
              Dawal is live
            </p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setIsMinimized(true)}
            className="hover:bg-slate-800 p-2 rounded-lg transition group"
            aria-label="Minimize"
            title="Minimize"
          >
            <Minimize2 className="w-5 h-5 group-hover:scale-110 transition" />
          </button>
          <button
            onClick={() => onClose ? onClose() : null}
            className="bg-white hover:bg-slate-100 p-2 rounded-lg transition group"
            aria-label="Close"
            title="Close chat"
          >
            <X className="w-5 h-5 text-slate-900 group-hover:scale-110 transition" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 sm:p-5 space-y-3 sm:space-y-4 bg-slate-50">
        {messages.map((msg, idx) => (
          <div
            key={idx}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[85%] rounded-2xl p-4 shadow-sm ${
                msg.role === 'user'
                  ? 'bg-blue-500 text-white'
                  : 'bg-white text-slate-900 border border-slate-200'
              }`}
            >
              <p className="text-sm leading-relaxed whitespace-pre-wrap">{msg.content}</p>
              <p className={`text-xs mt-2 ${msg.role === 'user' ? 'text-blue-100' : 'text-slate-400'}`}>
                {new Date().toLocaleTimeString('en-CA', { timeZone: 'America/Toronto', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
          </div>
        ))}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-white text-slate-900 border border-slate-200 rounded-2xl p-4 flex items-center gap-3 shadow-sm">
              <Loader2 className="w-4 h-4 animate-spin text-blue-500" />
              <span className="text-sm font-medium">Thinking...</span>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={sendMessage} className="p-3 sm:p-5 border-t border-slate-200 bg-white sm:rounded-b-2xl">
        <div className="flex gap-3">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type your message..."
            disabled={isLoading}
            className="flex-1 bg-slate-50 border-2 border-slate-300 text-slate-900 placeholder:text-slate-400 focus:border-blue-500 rounded-xl px-4 py-3"
          />
          <Button
            type="submit"
            disabled={isLoading || !input.trim()}
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-xl px-5 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Send className="w-5 h-5" />
          </Button>
        </div>
        <p className="text-xs text-slate-400 mt-2 sm:mt-3 text-center hidden sm:block">
          Dawal is live • Your conversations are secure
        </p>
      </form>
    </div>
  );
};
